/**
 * Board Flows Configuration - Builder Prime Status Parity
 * =======================================================
 * 
 * CRITICAL: This configuration implements VERBATIM Builder Prime status parity.
 * All status names match BP exactly - DO NOT modify status names.
 * 
 * Each flow has:
 * - id: Unique identifier for the flow
 * - name: Display name
 * - stages: Array of stage configurations
 * 
 * Each stage has:
 * - id: Stage identifier (used in database)
 * - name: Display name (VERBATIM from Builder Prime)
 * - color: Tailwind CSS color class
 * - description: Tooltip/help text
 * - isArchive: If true, stage is dimmed (for lost/dead stages)
 * - bpStatus: Original Builder Prime status name (for reference)
 */

// ============================================
// LEAD STATUSES - VERBATIM FROM BUILDER PRIME
// ============================================

// ACTIVE / PIPELINE STAGES
export const LEAD_PIPELINE_STAGES = [
  { 
    id: 'lead_received', 
    name: 'Lead Received', 
    bpStatus: 'Lead Received',
    color: 'bg-slate-100 dark:bg-slate-800',
    description: 'New incoming leads',
    category: 'pipeline'
  },
  { 
    id: 'lead_contacted', 
    name: 'Lead Contacted', 
    bpStatus: 'Lead Contacted',
    color: 'bg-blue-100 dark:bg-blue-900/30',
    description: 'Initial contact made',
    category: 'pipeline'
  },
  { 
    id: 'lead_self_booked', 
    name: 'Lead Self-Booked', 
    bpStatus: 'Lead Self-Booked',
    color: 'bg-indigo-100 dark:bg-indigo-900/30',
    description: 'Customer self-scheduled appointment',
    category: 'pipeline'
  },
  { 
    id: 'lead_needs_self_book_reschedule', 
    name: 'Lead Needs Self-Book Reschedule', 
    bpStatus: 'Lead Needs Self-Book Reschedule',
    color: 'bg-purple-100 dark:bg-purple-900/30',
    description: 'Lead needs to reschedule self-book',
    category: 'pipeline'
  },
  { 
    id: 'appointment_not_set', 
    name: 'Appointment Not Set', 
    bpStatus: 'Appointment Not Set',
    color: 'bg-yellow-100 dark:bg-yellow-900/30',
    description: 'No appointment scheduled yet',
    category: 'pipeline'
  },
  { 
    id: 'appointment_set', 
    name: 'Appointment Set', 
    bpStatus: 'Appointment Set',
    color: 'bg-cyan-100 dark:bg-cyan-900/30',
    description: 'Demo appointment scheduled',
    category: 'pipeline'
  },
  { 
    id: 'appointment_confirmed', 
    name: 'Appointment Confirmed', 
    bpStatus: 'Appointment Confirmed',
    color: 'bg-emerald-100 dark:bg-emerald-900/30',
    description: 'Appointment confirmed with customer',
    category: 'pipeline'
  },
  { 
    id: 'appointment_cancelled', 
    name: 'Appointment Cancelled', 
    bpStatus: 'Appointment Cancelled',
    color: 'bg-orange-100 dark:bg-orange-900/30',
    description: 'Appointment was cancelled',
    category: 'pipeline'
  },
  { 
    id: 'lead_issued', 
    name: 'Lead Issued', 
    bpStatus: 'Lead Issued',
    color: 'bg-blue-100 dark:bg-blue-900/30',
    description: 'Lead issued to sales rep',
    category: 'sales'
  },
  { 
    id: 'reset_needs_appointment', 
    name: 'Reset - Needs Appointment', 
    bpStatus: 'Reset - Needs Appointment',
    color: 'bg-slate-100 dark:bg-slate-800',
    description: 'Reset - needs new appointment',
    category: 'sales'
  },
  { 
    id: 'reset_with_appointment', 
    name: 'Reset - w/ Appointment', 
    bpStatus: 'Reset - w/ Appointment',
    color: 'bg-indigo-100 dark:bg-indigo-900/30',
    description: 'Reset - has appointment scheduled',
    category: 'sales'
  },
  { 
    id: 'reset_appt_cancelled', 
    name: 'Reset - Appt Cancelled', 
    bpStatus: 'Reset - Appt Cancelled',
    color: 'bg-orange-100 dark:bg-orange-900/30',
    description: 'Reset - appointment was cancelled',
    category: 'sales'
  },
  { 
    id: 'no_demo', 
    name: 'No Demo', 
    bpStatus: 'No Demo',
    color: 'bg-yellow-100 dark:bg-yellow-900/30',
    description: 'Demo not completed (no-show)',
    category: 'sales'
  },
  { 
    id: 'demo', 
    name: 'Demo', 
    bpStatus: 'Demo',
    color: 'bg-purple-100 dark:bg-purple-900/30',
    description: 'Demo scheduled or in progress',
    category: 'sales'
  },
  { 
    id: 'demo_no_sale', 
    name: 'Demo / No Sale', 
    bpStatus: 'Demo / No Sale',
    color: 'bg-gray-200 dark:bg-gray-800',
    description: 'Demo completed, no sale',
    category: 'sales',
    isArchive: true
  },
  { 
    id: 'estimate_sent', 
    name: 'Estimate Sent', 
    bpStatus: 'Estimate Sent',
    color: 'bg-cyan-100 dark:bg-cyan-900/30',
    description: 'Estimate/proposal sent to customer',
    category: 'sales'
  },
];

// SALES / CUSTOMER STATES
export const LEAD_SALES_STAGES = [
  { 
    id: 'job_sold', 
    name: 'Job Sold', 
    bpStatus: 'Job Sold',
    color: 'bg-emerald-100 dark:bg-emerald-900/30',
    description: 'Sale closed!',
    category: 'sales'
  },
  { 
    id: 'credit_reject', 
    name: 'Credit Reject', 
    bpStatus: 'Credit Reject',
    color: 'bg-red-200 dark:bg-red-900/30',
    description: 'Credit rejected',
    category: 'sales',
    isArchive: true
  },
  { 
    id: 'job_sold_cancelled', 
    name: 'Job Sold & Cancelled', 
    bpStatus: 'Job Sold & Cancelled',
    color: 'bg-red-100 dark:bg-red-800',
    description: 'Sale cancelled after closing',
    category: 'sales',
    isArchive: true
  },
  { 
    id: 'job_in_progress', 
    name: 'Job In Progress', 
    bpStatus: 'Job In Progress',
    color: 'bg-green-100 dark:bg-green-900/30',
    description: 'Handed off to operations',
    category: 'operations'
  },
  { 
    id: 'customer', 
    name: 'Customer', 
    bpStatus: 'Customer',
    color: 'bg-teal-100 dark:bg-teal-900/30',
    description: 'Completed customer',
    category: 'operations'
  },
];

// TEMP/MARKETING
export const LEAD_MARKETING_STAGES = [
  { 
    id: 'temp_marketing', 
    name: 'TEMP/MARKETING', 
    bpStatus: 'TEMP/MARKETING',
    color: 'bg-pink-100 dark:bg-pink-900/30',
    description: 'Temporary marketing hold',
    category: 'marketing'
  },
];

// DEAD STATES - All verbatim from BP
export const LEAD_DEAD_STAGES = [
  { 
    id: 'dead_declined_quote', 
    name: 'DEAD - Declined Quote - Do Not Call', 
    bpStatus: 'DEAD - Declined Quote - Do Not Call',
    color: 'bg-gray-200 dark:bg-gray-800',
    description: 'Declined quote, do not contact',
    category: 'dead',
    isArchive: true
  },
  { 
    id: 'dead_diy', 
    name: 'DEAD - DIY', 
    bpStatus: 'DEAD - DIY',
    color: 'bg-gray-200 dark:bg-gray-800',
    description: 'Customer doing it themselves',
    category: 'dead',
    isArchive: true
  },
  { 
    id: 'dead_interior', 
    name: 'DEAD - Interior Painting', 
    bpStatus: 'DEAD - Interior Painting',
    color: 'bg-gray-200 dark:bg-gray-800',
    description: 'Interior painting request (out of scope)',
    category: 'dead',
    isArchive: true
  },
  { 
    id: 'dead_cannot_coat', 
    name: 'DEAD - Cannot Coat', 
    bpStatus: 'DEAD - Cannot Coat',
    color: 'bg-gray-200 dark:bg-gray-800',
    description: 'Surface cannot be coated',
    category: 'dead',
    isArchive: true
  },
  { 
    id: 'dead_out_of_territory', 
    name: 'DEAD - Out of Territory', 
    bpStatus: 'DEAD - Out of Territory',
    color: 'bg-gray-200 dark:bg-gray-800',
    description: 'Outside service area',
    category: 'dead',
    isArchive: true
  },
  { 
    id: 'dead_shark', 
    name: 'DEAD - Shark Lead', 
    bpStatus: 'DEAD - Shark Lead',
    color: 'bg-gray-200 dark:bg-gray-800',
    description: 'Shark/spam lead',
    category: 'dead',
    isArchive: true
  },
  { 
    id: 'dead_employment', 
    name: 'DEAD - Employment Inquiry', 
    bpStatus: 'DEAD - Employment Inquiry',
    color: 'bg-gray-200 dark:bg-gray-800',
    description: 'Employment inquiry, not a lead',
    category: 'dead',
    isArchive: true
  },
  { 
    id: 'dead_ghosted', 
    name: 'DEAD - Ghosted/Bad Lead', 
    bpStatus: 'DEAD - Ghosted/Bad Lead',
    color: 'bg-gray-200 dark:bg-gray-800',
    description: 'Customer ghosted or bad lead',
    category: 'dead',
    isArchive: true
  },
  { 
    id: 'dead_duplicate', 
    name: 'DEAD - Duplicate', 
    bpStatus: 'DEAD - Duplicate',
    color: 'bg-gray-200 dark:bg-gray-800',
    description: 'Duplicate lead',
    category: 'dead',
    isArchive: true
  },
  { 
    id: 'existing_customer', 
    name: 'Existing Customer', 
    bpStatus: 'Existing Customer',
    color: 'bg-green-100 dark:bg-green-900/30',
    description: 'Existing customer inquiry',
    category: 'customer'
  },
  { 
    id: 'nurture_events', 
    name: 'Nurture - Events', 
    bpStatus: 'Nurture - Events',
    color: 'bg-pink-100 dark:bg-pink-900/30',
    description: 'In nurture sequence for events',
    category: 'marketing'
  },
];

// UNKNOWN/UNMAPPED STATUS STAGE (for admin visibility)
export const UNMAPPED_STAGE = {
  id: 'unmapped',
  name: 'Unmapped Status',
  bpStatus: null,
  color: 'bg-red-100 dark:bg-red-900/30 border-2 border-red-500',
  description: 'STATUS NOT RECOGNIZED - Requires admin attention',
  category: 'unknown',
  isUnmapped: true
};

// Combined ALL lead stages
export const ALL_LEAD_STAGES = [
  ...LEAD_PIPELINE_STAGES,
  ...LEAD_SALES_STAGES,
  ...LEAD_MARKETING_STAGES,
  ...LEAD_DEAD_STAGES,
  UNMAPPED_STAGE
];

// ============================================
// PROJECT STATUSES - VERBATIM FROM BUILDER PRIME
// ============================================
export const PROJECT_STAGES = [
  { 
    id: 'estimate_request', 
    name: 'Estimate Request', 
    bpStatus: 'Estimate Request',
    color: 'bg-slate-100 dark:bg-slate-800',
    description: 'Customer requested an estimate',
    category: 'sales'
  },
  { 
    id: 'estimate_sent', 
    name: 'Estimate Sent', 
    bpStatus: 'Estimate Sent',
    color: 'bg-blue-100 dark:bg-blue-900/30',
    description: 'Estimate sent to customer',
    category: 'sales'
  },
  { 
    id: 'customer_signed', 
    name: 'Customer Signed', 
    bpStatus: 'Customer Signed',
    color: 'bg-indigo-100 dark:bg-indigo-900/30',
    description: 'Contract signed by customer',
    category: 'sales'
  },
  { 
    id: 'sale_lost', 
    name: 'Sale Lost', 
    bpStatus: 'Sale Lost',
    color: 'bg-gray-200 dark:bg-gray-800',
    description: 'Sale was lost',
    category: 'sales',
    isArchive: true
  },
  { 
    id: 'sold', 
    name: 'Sold', 
    bpStatus: 'Sold',
    color: 'bg-purple-100 dark:bg-purple-900/30',
    description: 'Sale completed',
    category: 'sales'
  },
  { 
    id: 'deposit_received', 
    name: 'Deposit Received', 
    bpStatus: 'Deposit Received',
    color: 'bg-cyan-100 dark:bg-cyan-900/30',
    description: 'Deposit payment received',
    category: 'operations'
  },
  { 
    id: 'sale_contingent', 
    name: 'Sale Contingent', 
    bpStatus: 'Sale Contingent',
    color: 'bg-pink-100 dark:bg-pink-900/30',
    description: 'Sale is contingent',
    category: 'sales'
  },
  { 
    id: 'cancelled', 
    name: 'Cancelled', 
    bpStatus: 'Cancelled',
    color: 'bg-red-200 dark:bg-red-900/30',
    description: 'Project cancelled',
    category: 'sales',
    isArchive: true
  },
  { 
    id: 'contacted', 
    name: 'Contacted', 
    bpStatus: 'Contacted',
    color: 'bg-teal-100 dark:bg-teal-900/30',
    description: 'Customer contacted for scheduling',
    category: 'operations'
  },
  { 
    id: 'scheduled', 
    name: 'Scheduled', 
    bpStatus: 'Scheduled',
    color: 'bg-amber-100 dark:bg-amber-900/30',
    description: 'Installation date scheduled',
    category: 'operations'
  },
  { 
    id: 'in_progress', 
    name: 'In Progress', 
    bpStatus: 'In Progress',
    color: 'bg-orange-100 dark:bg-orange-900/30',
    description: 'Work is in progress',
    category: 'operations'
  },
  { 
    id: 'collections', 
    name: 'Collections (Complete, Need Payment)', 
    bpStatus: 'Collections (Complete, Need Payment)',
    color: 'bg-yellow-100 dark:bg-yellow-900/30',
    description: 'In collections for payment',
    category: 'operations'
  },
  { 
    id: 'complete', 
    name: 'Complete', 
    bpStatus: 'Complete',
    color: 'bg-emerald-100 dark:bg-emerald-900/30',
    description: 'Project completed',
    category: 'operations'
  },
  { 
    id: 'recoating', 
    name: 'Recoating', 
    bpStatus: 'Recoating',
    color: 'bg-green-100 dark:bg-green-900/30',
    description: 'Recoating project',
    category: 'operations'
  },
  UNMAPPED_STAGE
];

// ============================================
// STATUS MAPPING (BP Status Name → Stage ID)
// ============================================

// Build lead status mapping from stages
export const BP_LEAD_STATUS_TO_STAGE = {};
ALL_LEAD_STAGES.forEach(stage => {
  if (stage.bpStatus) {
    BP_LEAD_STATUS_TO_STAGE[stage.bpStatus] = stage.id;
  }
});

// Build project status mapping from stages
export const BP_PROJECT_STATUS_TO_STAGE = {};
PROJECT_STAGES.forEach(stage => {
  if (stage.bpStatus) {
    BP_PROJECT_STATUS_TO_STAGE[stage.bpStatus] = stage.id;
  }
});

// ============================================
// LEGACY COMPATIBILITY MAPPINGS
// (Maps old stage IDs to new ones - for backwards compatibility)
// ============================================
export const LEGACY_STAGE_MAPPING = {
  // Old call center mappings
  'new': 'lead_received',
  'lead': 'lead_received',
  'lead_recieved': 'lead_received',
  'contacted': 'lead_contacted',
  'lead_needs_self_book': 'lead_needs_self_book_reschedule',
  'callback': 'appointment_not_set',
  'callback_scheduled': 'appointment_not_set',
  'nurture': 'nurture_events',
  'temp_marketing': 'temp_marketing',
  'qualified': 'appointment_confirmed',
  'no_contact': 'dead_ghosted',
  'dnc': 'dead_declined_quote',
  'dead': 'dead_ghosted',
  
  // Old sales mappings
  'lead_assigned': 'lead_issued',
  'intro_call': 'lead_issued',
  'demo_scheduled': 'demo',
  'demo_confirmed': 'demo',
  'demo_completed': 'demo',
  'pending_signature': 'estimate_sent',
  'proposal_sent': 'estimate_sent',
  'sold': 'job_sold',
  'demo_sold': 'job_sold',
  'signed': 'job_sold',
  'signed_agreement': 'job_sold',
  'contract_signed': 'job_sold',
  'to_production': 'job_in_progress',
  'production': 'job_in_progress',
  'lost': 'demo_no_sale',
  'lost_dnq': 'demo_no_sale',
  'no_sale': 'demo_no_sale',
};

// ============================================
// FLOW DEFINITIONS
// ============================================

// Call Center Flow - Early pipeline stages
export const CALL_CENTER_STAGES = [
  ...LEAD_PIPELINE_STAGES.filter(s => 
    ['lead_received', 'lead_contacted', 'lead_self_booked', 'lead_needs_self_book_reschedule',
     'appointment_not_set', 'appointment_set', 'appointment_confirmed', 'appointment_cancelled'].includes(s.id)
  ),
  ...LEAD_DEAD_STAGES.filter(s => s.id === 'nurture_events' || s.id === 'existing_customer'),
  UNMAPPED_STAGE
];

// Sales Flow - Sales pipeline stages
// Sales Tracking Stages (Shadow Cards)
export const SALES_TRACKING_STAGES = [
  { 
    id: 'sold', 
    name: 'Sold', 
    color: 'bg-emerald-100 dark:bg-emerald-900/30',
    description: 'Sales tracking - active sales',
    category: 'tracking',
    isShadow: true
  },
  { 
    id: 'completed', 
    name: 'Completed', 
    color: 'bg-teal-100 dark:bg-teal-900/30',
    description: 'Sales tracking - current year completions',
    category: 'tracking',
    isShadow: true
  },
  { 
    id: 'archived', 
    name: 'Archived', 
    color: 'bg-gray-100 dark:bg-gray-800',
    description: 'Sales tracking - prior year sales',
    category: 'tracking',
    isArchive: true,
    isShadow: true
  }
];

export const SALES_STAGES = [
  // Lead pipeline stages for sales
  ...LEAD_PIPELINE_STAGES.filter(s => 
    ['lead_issued', 'reset_needs_appointment', 'reset_with_appointment', 'reset_appt_cancelled',
     'no_demo', 'demo', 'demo_no_sale'].includes(s.id)
  ),
  // Project sales stages: Estimate Sent → Estimate Request → Customer Signed → Job Sold
  { 
    id: 'estimate_sent', 
    name: 'Estimate Sent', 
    bpStatus: 'Estimate Sent',
    color: 'bg-blue-100 dark:bg-blue-900/30',
    description: 'Estimate sent to customer',
    category: 'sales'
  },
  { 
    id: 'estimate_request', 
    name: 'Estimate Request', 
    bpStatus: 'Estimate Request',
    color: 'bg-slate-100 dark:bg-slate-800',
    description: 'Customer requested an estimate',
    category: 'sales'
  },
  { 
    id: 'customer_signed', 
    name: 'Customer Signed', 
    bpStatus: 'Customer Signed',
    color: 'bg-indigo-100 dark:bg-indigo-900/30',
    description: 'Contract signed by customer',
    category: 'sales'
  },
  // Lead sales stages
  ...LEAD_SALES_STAGES,
  // Add Sales Tracking Stages for shadow cards
  ...SALES_TRACKING_STAGES,
  UNMAPPED_STAGE
];

// Operations Flow - Project stages (excluding sales stages)
export const OPERATIONS_STAGES = PROJECT_STAGES.filter(s => 
  !['estimate_request', 'estimate_sent', 'customer_signed'].includes(s.id)
);

export const BOARD_FLOWS = {
  call_center: {
    id: 'call_center',
    name: 'Call Center',
    stages: CALL_CENTER_STAGES,
    headerColor: 'bg-slate-600',
    description: 'Lead intake and appointment setting'
  },
  sales: {
    id: 'sales',
    name: 'Sales',
    stages: SALES_STAGES,
    headerColor: 'bg-blue-600',
    description: 'Sales pipeline and deal tracking'
  },
  operations: {
    id: 'operations',
    name: 'Operations',
    stages: OPERATIONS_STAGES,
    headerColor: 'bg-amber-600',
    description: 'Production and installation tracking'
  },
  full_flow: {
    id: 'full_flow',
    name: 'Full Lead Flow',
    stages: ALL_LEAD_STAGES,
    description: 'Complete journey from lead to customer'
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get the stage config for a record based on its BP status
 * CRITICAL: Uses BP status verbatim, returns unmapped stage if unknown
 */
export const getStageForBPStatus = (bpStatus, recordType = 'lead') => {
  if (!bpStatus) return UNMAPPED_STAGE;
  
  const mapping = recordType === 'project' ? BP_PROJECT_STATUS_TO_STAGE : BP_LEAD_STATUS_TO_STAGE;
  const stageId = mapping[bpStatus];
  
  if (!stageId) {
    console.warn(`[STATUS PARITY] Unknown ${recordType} status: "${bpStatus}"`);
    return { ...UNMAPPED_STAGE, displayStatus: bpStatus };
  }
  
  const stages = recordType === 'project' ? PROJECT_STAGES : ALL_LEAD_STAGES;
  return stages.find(s => s.id === stageId) || UNMAPPED_STAGE;
};

/**
 * Get normalized stage ID - with legacy fallback
 */
export const getNormalizedStage = (record, flowType) => {
  // First, try to use BP status directly
  const bpStatus = record.bp_status_name || record.lead_status || record.status;
  if (bpStatus) {
    const recordType = record.recordType === 'project' || flowType === 'operations' ? 'project' : 'lead';
    const mapping = recordType === 'project' ? BP_PROJECT_STATUS_TO_STAGE : BP_LEAD_STATUS_TO_STAGE;
    if (mapping[bpStatus]) {
      return mapping[bpStatus];
    }
  }
  
  // Fall back to existing stage field
  const stage = record.stage || record.status || 'unmapped';
  
  // Check legacy mapping
  if (LEGACY_STAGE_MAPPING[stage]) {
    return LEGACY_STAGE_MAPPING[stage];
  }
  
  return stage;
};

/**
 * Determine which flow a record belongs to based on its stage
 */
export const getRecordFlow = (record) => {
  const stage = getNormalizedStage(record);
  
  // Check each flow's stages
  if (CALL_CENTER_STAGES.find(s => s.id === stage)) return 'call_center';
  if (SALES_STAGES.find(s => s.id === stage)) return 'sales';
  if (OPERATIONS_STAGES.find(s => s.id === stage)) return 'operations';
  
  // Default based on record type
  if (record.recordType === 'project') return 'operations';
  if (!record.assigned_to) return 'call_center';
  return 'sales';
};

/**
 * Get stages for a specific flow
 */
export const getFlowStages = (flowType) => {
  return BOARD_FLOWS[flowType]?.stages || ALL_LEAD_STAGES;
};

/**
 * Get stage mapping for a specific flow type
 * Returns an object mapping stage IDs to stage configs
 */
export const getFlowStageMapping = (flowType) => {
  const stages = BOARD_FLOWS[flowType]?.stages || ALL_LEAD_STAGES;
  const mapping = {};
  stages.forEach(stage => {
    mapping[stage.id] = stage;
  });
  return mapping;
};

// ============================================
// LOCALSTORAGE KEYS
// ============================================
export const BOARD_STORAGE_KEYS = {
  FLOW_TYPE: 'rhino_board_flow_type',
  ENTITY_TYPE: 'rhino_board_entity_type',
};

// ============================================
// ENTITY TYPES
// ============================================
export const ENTITY_TYPES = {
  all: {
    id: 'all',
    name: 'All Records',
    collection: null,
    detailPath: (id, recordType) => recordType === 'lead' 
      ? `/workspace/records?source=board&mode=leads&id=${id}&view=detail`
      : `/workspace/records?source=board&mode=projects&id=${id}&view=detail`
  },
  leads: {
    id: 'leads',
    name: 'Leads',
    collection: 'leads',
    detailPath: (id) => `/workspace/records?source=board&mode=leads&id=${id}&view=detail`
  },
  projects: {
    id: 'projects', 
    name: 'Projects',
    collection: 'jobs',
    detailPath: (id) => `/workspace/records?source=board&mode=projects&id=${id}&view=detail`
  }
};

export default BOARD_FLOWS;
